import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DEFAULT_COLUMN_FILTERS,
  EMPTY_TOKEN,
} from "./meterReadingsHistoryConstants";

/**
 * Derive unique filter option values from rows.
 */
const deriveFilterOptions = (rows, lookups) => {
  if (!rows || rows.length === 0) {
    return {
      calendarYears: [],
      calendarMonths: [],
      meterIds: [],
      permitGroups: [],
      wells: [],
      permits: [],
      reportStatuses: [],
    };
  }

  const cy = new Set();
  const cm = new Set();
  const mi = new Set();
  const pg = new Set();
  const w = new Set();
  const p = new Set();
  const rs = new Set();

  let hasEmptyPg = false;
  let hasEmptyW = false;
  let hasEmptyP = false;

  for (const row of rows) {
    if (row.c_year != null) cy.add(String(row.c_year));
    if (row.c_month != null)
      cm.add(lookups.months[row.c_month] || String(row.c_month));
    if (row.meter_id) mi.add(row.meter_id);
    if (row.mr_report_status_ndx != null)
      rs.add(
        lookups.reportStatuses[row.mr_report_status_ndx] ||
          String(row.mr_report_status_ndx)
      );

    if (row.assoc_permit_groups && row.assoc_permit_groups.length > 0) {
      for (const ndx of row.assoc_permit_groups) {
        if (lookups.permitGroups[ndx]) pg.add(lookups.permitGroups[ndx]);
      }
    } else {
      hasEmptyPg = true;
    }

    if (row.assoc_wells && row.assoc_wells.length > 0) {
      for (const ndx of row.assoc_wells) {
        if (lookups.wells[ndx]) w.add(lookups.wells[ndx]);
      }
    } else {
      hasEmptyW = true;
    }

    if (row.assoc_permits && row.assoc_permits.length > 0) {
      for (const ndx of row.assoc_permits) {
        if (lookups.permits[ndx]) p.add(lookups.permits[ndx]);
      }
    } else {
      hasEmptyP = true;
    }
  }

  const sortStr = (a, b) => a.localeCompare(b);
  const sortNumDesc = (a, b) => Number(b) - Number(a);

  const pgArr = [...pg].sort(sortStr);
  if (hasEmptyPg) pgArr.unshift(EMPTY_TOKEN);

  const wArr = [...w].sort(sortStr);
  if (hasEmptyW) wArr.unshift(EMPTY_TOKEN);

  const pArr = [...p].sort(sortStr);
  if (hasEmptyP) pArr.unshift(EMPTY_TOKEN);

  return {
    calendarYears: [...cy].sort(sortNumDesc),
    calendarMonths: [...cm].sort(sortStr),
    meterIds: [...mi].sort(sortStr),
    permitGroups: pgArr,
    wells: wArr,
    permits: pArr,
    reportStatuses: [...rs].sort(sortStr),
  };
};

/**
 * Apply column filters to rows.
 */
const applyFilters = (rows, columnFilters, initialized, lookups) => {
  if (!rows || rows.length === 0) return [];
  if (!initialized) return rows;

  const cySet = new Set(columnFilters.calendarYears);
  const cmSet = new Set(columnFilters.calendarMonths);
  const miSet = new Set(columnFilters.meterIds);
  const rsSet = new Set(columnFilters.reportStatuses);
  const pgSet = new Set(columnFilters.permitGroups);
  const wSet = new Set(columnFilters.wells);
  const pSet = new Set(columnFilters.permits);

  if (
    cySet.size === 0 ||
    cmSet.size === 0 ||
    miSet.size === 0 ||
    rsSet.size === 0 ||
    pgSet.size === 0 ||
    wSet.size === 0 ||
    pSet.size === 0
  ) {
    return [];
  }

  const pgAllowEmpty = pgSet.has(EMPTY_TOKEN);
  const wAllowEmpty = wSet.has(EMPTY_TOKEN);
  const pAllowEmpty = pSet.has(EMPTY_TOKEN);

  return rows.filter((row) => {
    if (!cySet.has(String(row.c_year))) return false;
    if (!cmSet.has(lookups.months[row.c_month] || String(row.c_month)))
      return false;
    if (!miSet.has(row.meter_id)) return false;
    if (
      row.mr_report_status_ndx != null &&
      !rsSet.has(
        lookups.reportStatuses[row.mr_report_status_ndx] ||
          String(row.mr_report_status_ndx)
      )
    )
      return false;

    const pgArr = row.assoc_permit_groups;
    if (!pgArr || pgArr.length === 0) {
      if (!pgAllowEmpty) return false;
    } else {
      if (!pgArr.some((ndx) => pgSet.has(lookups.permitGroups[ndx])))
        return false;
    }

    const wArr = row.assoc_wells;
    if (!wArr || wArr.length === 0) {
      if (!wAllowEmpty) return false;
    } else {
      if (!wArr.some((ndx) => wSet.has(lookups.wells[ndx]))) return false;
    }

    const pArr = row.assoc_permits;
    if (!pArr || pArr.length === 0) {
      if (!pAllowEmpty) return false;
    } else {
      if (!pArr.some((ndx) => pSet.has(lookups.permits[ndx]))) return false;
    }

    return true;
  });
};

const getComparator = (order, orderBy) => {
  return (a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp =
      typeof aVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
    return order === "desc" ? -cmp : cmp;
  };
};

export const useColumnFilters = (rows, lookups) => {
  const [columnFilters, setColumnFilters] = useState(DEFAULT_COLUMN_FILTERS);
  const [initialized, setInitialized] = useState(false);
  const [orderBy, setOrderBy] = useState("c_year");
  const [order, setOrder] = useState("desc");
  const [searchText, setSearchText] = useState("");

  const filterOptions = useMemo(
    () => deriveFilterOptions(rows, lookups),
    [rows, lookups]
  );

  // Initialize column filters when data AND lookups are ready
  const lookupsReady =
    Object.keys(lookups.permitGroups).length > 0 &&
    Object.keys(lookups.wells).length > 0 &&
    Object.keys(lookups.permits).length > 0;

  useEffect(() => {
    if (
      filterOptions.calendarYears.length > 0 &&
      lookupsReady &&
      !initialized
    ) {
      const currentYear = new Date().getFullYear();
      const cyDefault = filterOptions.calendarYears.filter(
        (y) => Number(y) <= currentYear
      );

      setColumnFilters({
        calendarYears: cyDefault,
        calendarMonths: [...filterOptions.calendarMonths],
        meterIds: [...filterOptions.meterIds],
        permitGroups: [...filterOptions.permitGroups],
        wells: [...filterOptions.wells],
        permits: [...filterOptions.permits],
        reportStatuses: [...filterOptions.reportStatuses],
      });
      setInitialized(true);
    }
  }, [filterOptions, initialized, lookupsReady]);

  const handleFilterChange = useCallback((key, values) => {
    setColumnFilters((prev) => ({ ...prev, [key]: values }));
  }, []);

  const handleSort = useCallback(
    (columnId) => {
      setOrder((prev) => {
        if (orderBy === columnId && prev === "asc") return "desc";
        return "asc";
      });
      setOrderBy(columnId);
    },
    [orderBy]
  );

  const filteredAndSortedRows = useMemo(() => {
    let filtered = applyFilters(rows, columnFilters, initialized, lookups);

    if (searchText) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter((row) => {
        const fields = [
          row.c_year,
          lookups.months[row.c_month],
          row.meter_id,
          row.notes,
          row.prev_meter_reading,
          row.meter_reading,
          row.adjustment,
          row.calculated_gallons,
          lookups.adjustmentReasons[row.mr_adjustment_reason_ndx],
          lookups.reportStatuses[row.mr_report_status_ndx],
        ];
        if (row.assoc_permit_groups) {
          for (const ndx of row.assoc_permit_groups) {
            if (lookups.permitGroups[ndx])
              fields.push(lookups.permitGroups[ndx]);
          }
        }
        if (row.assoc_wells) {
          for (const ndx of row.assoc_wells) {
            if (lookups.wells[ndx]) fields.push(lookups.wells[ndx]);
          }
        }
        if (row.assoc_permits) {
          for (const ndx of row.assoc_permits) {
            if (lookups.permits[ndx]) fields.push(lookups.permits[ndx]);
          }
        }
        return fields.some(
          (v) => v != null && String(v).toLowerCase().includes(lower)
        );
      });
    }

    return filtered.sort(getComparator(order, orderBy));
  }, [rows, columnFilters, initialized, lookups, order, orderBy, searchText]);

  return {
    columnFilters,
    filterOptions,
    initialized,
    orderBy,
    order,
    searchText,
    setSearchText,
    handleFilterChange,
    handleSort,
    filteredAndSortedRows,
  };
};
